import { useState, useEffect, useCallback } from 'react';
import { apiKeys } from '../services/api';
import { confirmDelete, showError } from '../services/alerts';
import { IconKey } from './icons';

export default function ApiKeysPanel() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [revealedKey, setRevealedKey] = useState(null); // shown exactly once

  const load = useCallback(async () => {
    try {
      const { data } = await apiKeys.list();
      setKeys(data);
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to load API keys');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const { data } = await apiKeys.create(newName.trim());
      setRevealedKey(data);
      setNewName('');
      load();
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to create API key');
    } finally { setCreating(false); }
  };

  const handleRevoke = async (key) => {
    if (!(await confirmDelete(key.name))) return;
    try { await apiKeys.revoke(key.id); load(); }
    catch (err) { showError(err.response?.data?.error || 'Failed to revoke API key'); }
  };

  return (
    <div className="w-full max-w-2xl">
      <div className="mb-5 flex items-center gap-2">
        <IconKey />
        <div className="font-display text-base font-bold">API Keys</div>
      </div>
      <p className="mb-4 text-sm text-muted">
        Use an API key to trigger migrations from your own systems instead of the dashboard —
        send it as <code>Authorization: Bearer &lt;key&gt;</code>. Available on paid plans.
      </p>

      <div className="mb-5 flex gap-2">
        <input value={newName} onChange={e => setNewName(e.target.value)}
          placeholder="e.g. Production server"
          className="flex-1 rounded-md border border-border2 bg-surface2 px-3 py-2 text-[13px] text-ink outline-none focus:border-accent focus:ring-2 focus:ring-accent/20" />
        <button onClick={handleCreate} disabled={creating || !newName.trim()}
          className="rounded-md bg-accent px-4 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-accent2 disabled:cursor-not-allowed disabled:opacity-70">
          {creating ? 'Creating…' : '+ New API Key'}
        </button>
      </div>

      {loading ? null : keys.length === 0 ? (
        <div className="text-sm text-muted">No API keys yet.</div>
      ) : (
        <div className="divide-y divide-border2 rounded-lg border border-border2">
          {keys.map(k => (
            <div key={k.id} className="flex items-center justify-between px-4 py-3">
              <div>
                <div className="text-sm font-semibold text-ink">{k.name}</div>
                <div className="font-mono text-xs text-muted">{k.key_prefix}••••••••</div>
                <div className="text-xs text-dim">
                  Created {new Date(k.created_at).toLocaleDateString()}
                  {k.last_used_at ? ` · Last used ${new Date(k.last_used_at).toLocaleDateString()}` : ' · Never used'}
                </div>
              </div>
              <button onClick={() => handleRevoke(k)}
                className="rounded-md border border-border2 px-3 py-1.5 text-xs font-semibold text-bad transition-colors hover:border-bad">
                Revoke
              </button>
            </div>
          ))}
        </div>
      )}

      {revealedKey && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-lg rounded-2xl border border-border2 bg-surface p-7 shadow-2xl">
            <div className="mb-3 font-display text-lg font-bold text-ink">API key created</div>
            <p className="mb-3 text-sm text-warn">
              Copy this now — you won't be able to see it again. If you lose it, revoke it and create a new one.
            </p>
            <div className="mb-4 flex items-center gap-2 rounded-md border border-border2 bg-surface2 px-3 py-2 font-mono text-xs text-ink">
              <span className="flex-1 break-all">{revealedKey.key}</span>
              <button onClick={() => navigator.clipboard.writeText(revealedKey.key)}
                className="shrink-0 rounded-md border border-border2 px-2 py-1 text-[11px] font-semibold text-muted transition-colors hover:text-ink">
                Copy
              </button>
            </div>
            <button onClick={() => setRevealedKey(null)}
              className="w-full rounded-md bg-accent px-4 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-accent2">
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
